"use strict";
import { useQuery } from "@tanstack/react-query";
import { Token } from "@uniswap/sdk-core";
import { useMemo } from "react";
import { useTopazPoolFeeDetailsByAddress } from "components/lists/items/pools/useTopazPoolFeeDetailsByAddress";
import { OnchainItemListOptionType } from "components/lists/items/types";
import { TICK_SPACING_TO_FEE } from "constants/bscContracts";
import { getTokenLogoUrl } from "constants/tokenLogos";
import { useTokenLists } from "contexts/TokenListsContext";
import { ProtocolVersion } from "data/graphql/uniswap-data-api/__generated__/types-and-hooks";
import { UniverseChainId } from "features/chains/types";
import { fetchTopPoolsByTVL } from "features/routing/api/bscSubgraph";
function createCurrencyInfo(tokenData, chainId, logoFallback) {
  const token = new Token(chainId, tokenData.id, parseInt(tokenData.decimals, 10), tokenData.symbol, tokenData.name);
  return {
    currency: token,
    currencyId: `${chainId}-${tokenData.id.toLowerCase()}`,
    logoUrl: getTokenLogoUrl(tokenData.id) ?? logoFallback?.(tokenData.id) ?? null
  };
}
function transformV2PoolToPoolOption(pool, chainId, gaugeAddress, logoFallback, feeDetails) {
  return {
    type: OnchainItemListOptionType.Pool,
    poolId: pool.id,
    chainId,
    token0CurrencyInfo: createCurrencyInfo(pool.token0, chainId, logoFallback),
    token1CurrencyInfo: createCurrencyInfo(pool.token1, chainId, logoFallback),
    protocolVersion: ProtocolVersion.V2,
    feeTier: pool.stable ? 500 : 3e3,
    feeDetails,
    gaugeAddress
  };
}
function transformV3PoolToPoolOption(pool, chainId, gaugeAddress, logoFallback, feeDetails) {
  const tickSpacing = parseInt(pool.tickSpacing, 10);
  const feeTier = TICK_SPACING_TO_FEE[tickSpacing] || 3e3;
  return {
    type: OnchainItemListOptionType.Pool,
    poolId: pool.id,
    chainId,
    token0CurrencyInfo: createCurrencyInfo(pool.token0, chainId, logoFallback),
    token1CurrencyInfo: createCurrencyInfo(pool.token1, chainId, logoFallback),
    protocolVersion: ProtocolVersion.V3,
    feeTier,
    feeDetails,
    gaugeAddress
  };
}
export function useTrendingPoolsOptions(limit = 15, chainFilter = null, enabled = true) {
  const chainId = chainFilter || UniverseChainId.Mainnet;
  const { getTokenListLogoUrl } = useTokenLists();
  const { feeDetailsByAddress, isLoading: feeDetailsLoading } = useTopazPoolFeeDetailsByAddress(chainId, enabled);
  const {
    data: poolsData,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ["trending-pools", limit, chainId],
    queryFn: () => fetchTopPoolsByTVL(limit),
    enabled,
    staleTime: 5 * 60 * 1e3,
    // 5 minutes
    refetchInterval: 5 * 60 * 1e3
    // Refetch every 5 minutes
  });
  const poolOptions = useMemo(() => {
    if (!poolsData) {
      return void 0;
    }
    const allPools = [];
    poolsData.v2Pools.forEach((pool) => {
      const gaugeAddress = poolsData.gauges.get(pool.id.toLowerCase());
      const feeDetails = feeDetailsByAddress.get(pool.id.toLowerCase());
      allPools.push(transformV2PoolToPoolOption(pool, chainId, gaugeAddress, getTokenListLogoUrl, feeDetails));
    });
    poolsData.v3Pools.forEach((pool) => {
      const gaugeAddress = poolsData.gauges.get(pool.id.toLowerCase());
      const feeDetails = feeDetailsByAddress.get(pool.id.toLowerCase());
      allPools.push(transformV3PoolToPoolOption(pool, chainId, gaugeAddress, getTokenListLogoUrl, feeDetails));
    });
    return allPools.slice(0, limit);
  }, [poolsData, chainId, limit, getTokenListLogoUrl, feeDetailsByAddress]);
  return {
    data: poolOptions,
    loading: enabled && (isLoading || feeDetailsLoading),
    error: error ?? void 0,
    refetch
  };
}
